// eslint-disable-next-line
import util from "util";

import React, {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import * as MuiCore from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from "@material-ui/icons";

const styles = (theme) => ({
  oddRow: {
    backgroundColor: theme.palette.grey[200],
    "& :hover": {
      backgroundColor: theme.palette.grey[100],
    },
  },

  evenRow: {
    backgroundColor: "#fff",
    "& :hover": {
      backgroundColor: theme.palette.grey[100],
    },
  },
  highlightRow: {
    backgroundColor: `${theme.brandingColors.rubyDarkPurple} !important`,
    "& :hover": {
      backgroundColor: `${theme.brandingColors.rubyDarkPurple} !important`,
    },
  },
  checkBox: {
    color: theme.brandingColors.rubyDarkPurple,
    "& .MuiSvgIcon-root": {
      fill: theme.brandingColors.rubyDarkPurple,
    },
    "& .MuiTouchRipple-root": {
      color: theme.brandingColors.rubyDarkPurple,
    },
  },
  tableHeader: {
    // backgroundColor: "#a9a1b6",
  },
  tableHeaderCell: {
    fontFamily: "Sofia-bold",
    fontSize: 13,
    textTransform: "capitalize",
  },
  tableCell: {
    fontFamily: "Sofia",
    backgroundColor: "transparent",
    "& :hover": {
      backgroundColor: "transparent",
    },
  },
  navigationContainer: {
    justifyContent: "space-between",
  },
  navigationButton: {
    padding: "0",
  },
});
const EmptyState = memo(() => (
  <MuiCore.Grid style={{ width: "100%", textAlign: "center", marginTop: 150 }}>
    <MuiCore.SvgIcon
      viewBox="0 0 110 79.859"
      style={{ width: 110, height: "79.859" }}
    >
      <path
        style={{ color: "#e5136c" }}
        d="M158.386,344.842A10.581,10.581,0,1,1,147.8,334.268a10.578,10.578,0,0,1,10.582,10.574"
        transform="translate(-48.386 -331.962)"
      />
      <path
        style={{ color: "#7f2062" }}
        d="M92.294,342.924l8.312,8.309c8.289-8.284,21.511-8.546,29.475-.589l8.312-8.309c-12.548-12.538-33.228-12.275-46.1.589"
        transform="translate(-92.294 -333.101)"
      />
      <path
        style={{ color: "#ee9ca1" }}
        d="M137.141,350.034a8.713,8.713,0,1,1-8.712-8.708,8.71,8.71,0,0,1,8.712,8.708"
        transform="translate(-65.496 -325.073)"
      />
      <path
        style={{ color: "#e5136c" }}
        d="M105.86,356.031a34.571,34.571,0,0,0,69.142,0Z"
        transform="translate(-79.036 -310.721)"
      />
    </MuiCore.SvgIcon>
    <MuiCore.Typography
      style={{ fontFamily: "Sofia-bold", fontSize: 28, marginTop: 20 }}
    >
      You're all caught up!
    </MuiCore.Typography>
  </MuiCore.Grid>
));

/**
 * Do not use rowClickHighlight and enableSelect together
 */
const DataTable = forwardRef(
  (
    {
      classes,
      columns,
      data,
      getRowId,
      rowClickHighlight = false,
      enableSelect = false,
      enableNoContentPlaceholder = false,
      onSelectComponent = null,
      onSelectionChange = (rows, ids) => {},
      onRowClick = null,
      marginTop = 30,
      paginate = false,
    },
    ref
  ) => {
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const pageSizes = [5, 10, 15, 20];
    const [currentPageSize, setCurrentPageSize] = useState(pageSizes[0]);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const getRowIdentifier = useCallback(
      (row, index) => {
        if (getRowId) return getRowId(row);
        return index;
      },
      [getRowId]
    );

    const getSelectedRowData = useCallback(
      (selectedIds) => {
        const selectedSet = new Set(selectedIds);
        return data.reduce((acc, curr, index) => {
          const id = getRowIdentifier(curr, index);
          if (selectedSet.has(id)) {
            acc.push(curr);
          }
          return acc;
        }, []);
        //   return data.filter((row) => selectedSet.has(getRowIdentifier(row)));
      },
      [data, getRowIdentifier]
    );
    const propogateUpdate = useCallback(
      (selectedRows) => {
        const selectedIds = Array.from(selectedRows);
        onSelectionChange(selectedIds, getSelectedRowData(selectedIds));
      },
      [onSelectionChange, getSelectedRowData]
    );
    const handleRowSelect = useCallback(
      (e, rowId) => {
        e.stopPropagation();
        const newSelectedRows = new Set(selectedRows);
        if (selectedRows.has(rowId)) {
          newSelectedRows.delete(rowId);
        } else {
          newSelectedRows.add(rowId);
        }

        setSelectedRows(newSelectedRows);
        propogateUpdate(newSelectedRows);
        //   const selectedIds = Array.from(newSelectedRows);
        //   onSelectionChange(selectedIds, getSelectedRowData(selectedIds));
      },
      [selectedRows, propogateUpdate]
    );

    const handleSelectAll = useCallback(
      (e) => {
        e.stopPropagation();
        const newSelectedRows = new Set();
        // Deselect All rows
        if (selectedRows.size >= data.length) {
          setSelectedRows(newSelectedRows);
        } else {
          // Select All Rows
          data.forEach((row, index) => {
            newSelectedRows.add(getRowIdentifier(row, index));
          });
          setSelectedRows(newSelectedRows);
          // const selectedIds = Array.from(newSelectedRows);
          // onSelectionChange(selectedIds, getSelectedRowData(selectedIds));
        }
        propogateUpdate(newSelectedRows);
      },
      [selectedRows, data, getRowIdentifier, propogateUpdate]
    );
    const isSelected = (rowId) => selectedRows.has(rowId);
    useImperativeHandle(ref, () => ({
      resetSelection: () => {
        setSelectedRows(new Set());
      },
    }));
    const handleRowClick = (row, rowId) => {
      if (rowClickHighlight) {
        setSelectedRows(new Set().add(rowId));
      }
      if (typeof onRowClick === "function") {
        onRowClick(row);
      }
    };
    const renderData = useMemo(() => {
      if (!paginate) {
        return data;
      }
      const startIndex = currentPageIndex * currentPageSize;
      const endIndex = startIndex + currentPageSize;
      return data?.slice(startIndex, endIndex);
    }, [data, paginate, currentPageIndex, currentPageSize]);

    if (enableNoContentPlaceholder && !renderData?.length) {
      return <EmptyState />;
    }
    return (
      <React.Fragment>
        <MuiCore.TableContainer component={MuiCore.Paper} style={{ marginTop }}>
          <MuiCore.Table sx={{ minWidth: 700 }} size="small">
            <MuiCore.TableHead>
              <MuiCore.TableRow className={classes.tableHeader}>
                {enableSelect && (
                  <MuiCore.TableCell className={classes.tableHeaderCell}>
                    <div>
                      <MuiCore.Checkbox
                        size="small"
                        className={classes.checkBox}
                        onClick={handleSelectAll}
                        checked={
                          !!selectedRows.size &&
                          selectedRows.size === data.length
                        }
                      />
                      <span>
                        Select All{" "}
                        {!!selectedRows.size && `(${selectedRows.size})`}
                      </span>
                      {!!selectedRows.size &&
                        onSelectComponent &&
                        onSelectComponent()}
                    </div>
                  </MuiCore.TableCell>
                )}
                {columns.map((item) => {
                  return (
                    <MuiCore.TableCell className={classes.tableHeaderCell}>
                      {item.heading}
                    </MuiCore.TableCell>
                  );
                })}
              </MuiCore.TableRow>
            </MuiCore.TableHead>
            <MuiCore.TableBody>
              {renderData !== undefined &&
                renderData.map((row, index) => {
                  const rowId = getRowIdentifier(row, index);
                  const isRowSelected = isSelected(rowId, index);
                  return (
                    <MuiCore.TableRow
                      style={{ cursor: onRowClick ? "pointer" : "auto" }}
                      onClick={() => handleRowClick(row, rowId)}
                      key={index}
                      hover
                      className={
                        rowClickHighlight && isRowSelected
                          ? classes.highlightRow
                          : index % 2 === 0
                          ? classes.evenRow
                          : classes.oddRow
                      }
                    >
                      {enableSelect && (
                        <MuiCore.TableCell className={classes.tableCell}>
                          <MuiCore.Checkbox
                            size="small"
                            className={classes.checkBox}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => handleRowSelect(e, rowId)}
                            checked={isRowSelected}
                          />
                        </MuiCore.TableCell>
                      )}
                      {columns.map((column) => {
                        if (typeof column?.renderCell === "function") {
                          return (
                            <MuiCore.TableCell
                              style={{
                                textAlign: column.textAlign
                                  ? column.textAlign
                                  : "center",
                              }}
                              className={classes.tableCell}
                            >
                              {column.renderCell(row, isRowSelected)}
                            </MuiCore.TableCell>
                          );
                        }
                        return (
                          <MuiCore.TableCell className={classes.tableCell}>
                            {row[column.key]}
                          </MuiCore.TableCell>
                        );
                      })}
                    </MuiCore.TableRow>
                  );
                })}
            </MuiCore.TableBody>
          </MuiCore.Table>
        </MuiCore.TableContainer>
        {paginate && (
          <Navigation
            classes={classes}
            rowCount={data?.length ?? 0}
            currentPageIndex={currentPageIndex}
            setCurrentPageIndex={setCurrentPageIndex}
            currentPageSize={currentPageSize}
            setCurrentPageSize={setCurrentPageSize}
            pageSizes={pageSizes}
          />
        )}
      </React.Fragment>
    );
  }
);

const Navigation = ({
  classes,
  currentPageIndex,
  setCurrentPageIndex,
  currentPageSize,
  setCurrentPageSize,
  pageSizes,
  rowCount = 0,
}) => {
  const totalPages = Math.max(1, Math.ceil(rowCount / currentPageSize));
  const hasNextPage = useMemo(
    () => currentPageIndex + 1 <= totalPages - 1,
    [currentPageIndex, totalPages]
  );
  const hasPrevPage = useMemo(
    () => currentPageIndex - 1 >= 0,
    [currentPageIndex]
  );
  const handleNextPageNavigation = (pageIndex) => {
    setCurrentPageIndex(pageIndex);
  };
  const handlePageSizeChange = (e) => {
    setCurrentPageIndex(0);
    setCurrentPageSize(e.target.value);
  };
  return (
    <MuiCore.Grid container alignItems="center" style={{ marginTop: "10px" }}>
      <MuiCore.Grid container item xs={8}>
        <MuiCore.Typography className={classes.tableCell}>
          {rowCount} row(s)
        </MuiCore.Typography>
      </MuiCore.Grid>
      <MuiCore.Grid
        container
        item
        xs={4}
        wrap="nowrap"
        justifyContent="flex-end"
        alignItems="center"
        spacing={2}
      >
        <MuiCore.Grid item>
          <MuiCore.FormControl>
            <MuiCore.Select
              value={currentPageSize}
              onChange={handlePageSizeChange}
            >
              {pageSizes.map((pageSize) => (
                <MuiCore.MenuItem value={pageSize}>{pageSize}</MuiCore.MenuItem>
              ))}
            </MuiCore.Select>
          </MuiCore.FormControl>
        </MuiCore.Grid>
        <MuiCore.Grid item>
          <MuiCore.Typography
            style={{ textWrap: "nowrap" }}
            className={classes.tableCell}
          >
            Page {currentPageIndex + 1} of {totalPages}
          </MuiCore.Typography>
        </MuiCore.Grid>
        {/* Navigation Buttons */}
        <MuiCore.Grid
          item
          container
          wrap="nowrap"
          justifyContent="flex-end"
          style={{ width: "auto" }}
        >
          <MuiCore.Grid item>
            <MuiCore.IconButton
              disabled={!hasPrevPage}
              onClick={() => handleNextPageNavigation(0)}
              className={classes.navigationButton}
            >
              <KeyboardArrowLeft />
              <KeyboardArrowLeft style={{ marginLeft: "-15px" }} />
            </MuiCore.IconButton>
          </MuiCore.Grid>
          <MuiCore.Grid item>
            <MuiCore.IconButton
              onClick={() => handleNextPageNavigation(currentPageIndex - 1)}
              disabled={!hasPrevPage}
              className={classes.navigationButton}
            >
              <KeyboardArrowLeft />
            </MuiCore.IconButton>
          </MuiCore.Grid>
          <MuiCore.Grid item>
            <MuiCore.IconButton
              onClick={() => handleNextPageNavigation(currentPageIndex + 1)}
              disabled={!hasNextPage}
              className={classes.navigationButton}
            >
              <KeyboardArrowRight />
            </MuiCore.IconButton>
          </MuiCore.Grid>
          <MuiCore.Grid item>
            <MuiCore.IconButton
              onClick={() => handleNextPageNavigation(totalPages - 1)}
              disabled={!hasNextPage}
              className={classes.navigationButton}
            >
              <KeyboardArrowRight />
              <KeyboardArrowRight style={{ marginLeft: "-15px" }} />
            </MuiCore.IconButton>
          </MuiCore.Grid>
        </MuiCore.Grid>
      </MuiCore.Grid>
    </MuiCore.Grid>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  tableData: PropTypes.arrayOf(PropTypes.object).isRequired,
  actions: PropTypes.arrayOf(PropTypes.object),
  tableClass: PropTypes.object,
  stickyHeader: PropTypes.bool,
  size: PropTypes.string,
};

DataTable.defaultProps = {
  columns: [],
  tableData: [],
  actions: [],
  tableClass: {},
  stickyHeader: false,
  size: "medium",
};

export default withStyles(styles)(DataTable);
